import React, { useEffect, useState } from "react";

const FinanceTable = ({
    finances = [],
    loading = false,
    onEdit,
    onDelete
}) => {

    // =====================================================
    // PAGINATION
    // =====================================================

    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 10;


    // =====================================================
    // RESET PAGINATION
    // =====================================================

    useEffect(() => {

        setCurrentPage(1);

    }, [finances]);


    // =====================================================
    // FORMAT DATE
    // =====================================================

    const formatDate = (date) => {

        if (!date) {
            return "-";
        }

        const d = new Date(date);

        if (Number.isNaN(d.getTime())) {
            return "-";
        }

        return d.toLocaleDateString("fr-FR");
    };


    // =====================================================
    // FORMAT HEURE
    // =====================================================

    const formatHeure = (heure) => {

        if (!heure) {
            return "-";
        }

        return String(heure).substring(0, 5);
    };


    // =====================================================
    // FORMAT MONTANT
    // =====================================================

    const formatMontant = (montant) => {

        if (
            montant === null ||
            montant === undefined ||
            montant === ""
        ) {
            return "0,00 CAD";
        }

        const nombre = Number(montant);

        if (Number.isNaN(nombre)) {
            return "0,00 CAD";
        }

        return nombre.toLocaleString(
            "fr-FR",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        ) + " CAD";
    };


    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (
            <div className="paiement-table-loading">
                Chargement des finances...
            </div>
        );
    }


    // =====================================================
    // VIDE
    // =====================================================

    if (!finances.length) {

        return (
            <div className="paiement-table-empty">

                <div className="empty-icon">
                    💰
                </div>

                <h3>
                    Aucune finance
                </h3>

                <p>
                    Aucune finance n'a été enregistrée
                    pour le moment.
                </p>

            </div>
        );
    }


    // =====================================================
    // PAGINATION
    // =====================================================

    const totalItems = finances.length;

    const totalPages = Math.ceil(
        totalItems / itemsPerPage
    );

    const startIndex =
        (currentPage - 1) * itemsPerPage;

    const endIndex =
        startIndex + itemsPerPage;

    const financesPage =
        finances.slice(
            startIndex,
            endIndex
        );


    // =====================================================
    // CHANGER DE PAGE
    // =====================================================

    const changerPage = (page) => {

        if (
            page < 1 ||
            page > totalPages
        ) {
            return;
        }

        setCurrentPage(page);

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    // =====================================================
    // NUMÉROS DE PAGES
    // =====================================================

    const getPages = () => {

        const pages = [];

        for (
            let page = 1;
            page <= totalPages;
            page++
        ) {
            pages.push(page);
        }

        return pages;
    };


    // =====================================================
    // INFORMATIONS PAGINATION
    // =====================================================

    const premierElement =
        startIndex + 1;

    const dernierElement =
        Math.min(
            endIndex,
            totalItems
        );


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <div className="paiement-table-container">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="paiement-table-header">

                <div>

                    <h2>
                        Liste des finances
                    </h2>

                    <span>

                        {totalItems} finance
                        {totalItems > 1 ? "s" : ""}

                    </span>

                </div>

            </div>


            {/* =================================================
                VERSION DESKTOP
            ================================================= */}

            <div className="finance-desktop-table">

                <div className="table-responsive">

                    <table className="paiement-table">

                        <thead>

                            <tr>

                                <th>
                                    Rendez-vous
                                </th>

                                <th>
                                    Client
                                </th>

                                <th>
                                    Date RDV
                                </th>

                                <th>
                                    Heure
                                </th>

                                <th>
                                    Prix HT
                                </th>

                                <th>
                                    Coût matière
                                </th>

                                <th>
                                    Main-d'œuvre
                                </th>

                                <th>
                                    TPS / TVQ
                                </th>

                                <th>
                                    Prix TTC
                                </th>

                                <th>
                                    Profit
                                </th>

                                <th>
                                    Actions
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {financesPage.map(
                                (finance) => (

                                    <tr
                                        key={finance.id}
                                    >

                                        {/* RDV */}

                                        <td>
                                            <strong>
                                                RDV #
                                                {finance.rendez_vous_id}
                                            </strong>
                                        </td>


                                        {/* CLIENT */}

                                        <td>

                                            <div className="client-info">

                                                <strong>
                                                    {
                                                        finance.client_nom
                                                        || "-"
                                                    }
                                                </strong>

                                                {finance.client_telephone && (

                                                    <small>
                                                        {
                                                            finance.client_telephone
                                                        }
                                                    </small>

                                                )}

                                            </div>

                                        </td>


                                        {/* DATE */}

                                        <td>
                                            {
                                                formatDate(
                                                    finance.date_rdv
                                                )
                                            }
                                        </td>


                                        {/* HEURE */}

                                        <td>

                                            {
                                                formatHeure(
                                                    finance.heure_debut
                                                )
                                            }

                                            {" - "}

                                            {
                                                formatHeure(
                                                    finance.heure_fin
                                                )
                                            }

                                        </td>


                                        {/* HT */}

                                        <td>

                                            <strong>
                                                {
                                                    formatMontant(
                                                        finance.prix_ht
                                                    )
                                                }
                                            </strong>

                                        </td>


                                        {/* MATIERE */}

                                        <td>

                                            {
                                                formatMontant(
                                                    finance.cout_matiere
                                                )
                                            }

                                        </td>


                                        {/* MAIN OEUVRE */}

                                        <td>

                                            {
                                                formatMontant(
                                                    finance.main_oeuvre
                                                )
                                            }

                                        </td>


                                        {/* TPS TVQ */}

                                        <td>

                                            {
                                                formatMontant(
                                                    finance.tps_tvq
                                                )
                                            }

                                        </td>


                                        {/* TTC */}

                                        <td>

                                            <strong>
                                                {
                                                    formatMontant(
                                                        finance.prix_ttc
                                                    )
                                                }
                                            </strong>

                                        </td>


                                        {/* PROFIT */}

                                        <td>

                                            <strong>
                                                {
                                                    formatMontant(
                                                        finance.profit
                                                    )
                                                }
                                            </strong>

                                        </td>


                                        {/* ACTIONS */}

                                        <td>

                                            <div className="paiement-actions">

                                                <button
                                                    type="button"
                                                    className="btn-edit"
                                                    title="Modifier"
                                                    onClick={() =>
                                                        onEdit &&
                                                        onEdit(
                                                            finance
                                                        )
                                                    }
                                                >
                                                    ✏️
                                                </button>


                                                <button
                                                    type="button"
                                                    className="btn-delete"
                                                    title="Supprimer"
                                                    onClick={() =>
                                                        onDelete &&
                                                        onDelete(
                                                            finance.id
                                                        )
                                                    }
                                                >
                                                    🗑️
                                                </button>

                                            </div>

                                        </td>

                                    </tr>

                                )
                            )}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* =================================================
                VERSION MOBILE
            ================================================= */}

            <div className="finance-mobile-list">

                {financesPage.map(
                    (finance) => (

                        <div
                            className="finance-mobile-card"
                            key={finance.id}
                        >

                            {/* ============================
                                HEADER CARD
                            ============================ */}

                            <div className="finance-mobile-card-header">

                                <div>

                                    <strong className="finance-mobile-rdv">

                                        RDV #
                                        {finance.rendez_vous_id}

                                    </strong>

                                    <span className="finance-mobile-client">

                                        {finance.client_nom || "-"}

                                    </span>

                                </div>


                                <div className="finance-mobile-actions">

                                    <button
                                        type="button"
                                        className="btn-edit"
                                        title="Modifier"
                                        onClick={() =>
                                            onEdit &&
                                            onEdit(finance)
                                        }
                                    >
                                        ✏️
                                    </button>


                                    <button
                                        type="button"
                                        className="btn-delete"
                                        title="Supprimer"
                                        onClick={() =>
                                            onDelete &&
                                            onDelete(finance.id)
                                        }
                                    >
                                        🗑️
                                    </button>

                                </div>

                            </div>


                            {/* ============================
                                DATE / HEURE
                            ============================ */}

                            <div className="finance-mobile-info">

                                <div className="finance-mobile-row">

                                    <span>
                                        📅 Date
                                    </span>

                                    <strong>
                                        {
                                            formatDate(
                                                finance.date_rdv
                                            )
                                        }
                                    </strong>

                                </div>


                                <div className="finance-mobile-row">

                                    <span>
                                        🕐 Heure
                                    </span>

                                    <strong>

                                        {
                                            formatHeure(
                                                finance.heure_debut
                                            )
                                        }

                                        {" - "}

                                        {
                                            formatHeure(
                                                finance.heure_fin
                                            )
                                        }

                                    </strong>

                                </div>


                                {finance.client_telephone && (

                                    <div className="finance-mobile-row">

                                        <span>
                                            📞 Téléphone
                                        </span>

                                        <strong>
                                            {
                                                finance.client_telephone
                                            }
                                        </strong>

                                    </div>

                                )}

                            </div>


                            {/* ============================
                                FINANCE
                            ============================ */}

                            <div className="finance-mobile-financial">

                                <div className="finance-mobile-financial-row">

                                    <span>
                                        Prix HT
                                    </span>

                                    <strong>
                                        {
                                            formatMontant(
                                                finance.prix_ht
                                            )
                                        }
                                    </strong>

                                </div>


                                <div className="finance-mobile-financial-row">

                                    <span>
                                        Coût matière
                                    </span>

                                    <strong>
                                        {
                                            formatMontant(
                                                finance.cout_matiere
                                            )
                                        }
                                    </strong>

                                </div>


                                <div className="finance-mobile-financial-row">

                                    <span>
                                        Main-d'œuvre
                                    </span>

                                    <strong>
                                        {
                                            formatMontant(
                                                finance.main_oeuvre
                                            )
                                        }
                                    </strong>

                                </div>


                                <div className="finance-mobile-financial-row">

                                    <span>
                                        TPS / TVQ
                                    </span>

                                    <strong>
                                        {
                                            formatMontant(
                                                finance.tps_tvq
                                            )
                                        }
                                    </strong>

                                </div>


                                <div className="finance-mobile-financial-row finance-mobile-total">

                                    <span>
                                        Prix TTC
                                    </span>

                                    <strong>
                                        {
                                            formatMontant(
                                                finance.prix_ttc
                                            )
                                        }
                                    </strong>

                                </div>


                                <div className="finance-mobile-financial-row finance-mobile-profit">

                                    <span>
                                        💰 Profit
                                    </span>

                                    <strong>
                                        {
                                            formatMontant(
                                                finance.profit
                                            )
                                        }
                                    </strong>

                                </div>

                            </div>

                        </div>

                    )
                )}

            </div>


            {/* =================================================
                PAGINATION
            ================================================= */}

            {totalPages > 1 && (

                <div className="paiement-pagination">


                    {/* INFORMATIONS */}

                    <div className="pagination-info">

                        Affichage de{" "}

                        <strong>
                            {premierElement}
                        </strong>

                        {" à "}

                        <strong>
                            {dernierElement}
                        </strong>

                        {" sur "}

                        <strong>
                            {totalItems}
                        </strong>

                        {" finances"}

                    </div>


                    {/* CONTROLES */}

                    <div className="pagination-controls">


                        {/* PRÉCÉDENT */}

                        <button
                            type="button"
                            className="pagination-btn"
                            disabled={
                                currentPage === 1
                            }
                            onClick={() =>
                                changerPage(
                                    currentPage - 1
                                )
                            }
                        >

                            ‹

                            <span>
                                Précédent
                            </span>

                        </button>


                        {/* NUMÉROS */}

                        <div className="pagination-pages">

                            {getPages().map(
                                (page) => (

                                    <button
                                        key={page}
                                        type="button"
                                        className={
                                            `pagination-page ${
                                                currentPage === page
                                                    ? "active"
                                                    : ""
                                            }`
                                        }
                                        onClick={() =>
                                            changerPage(
                                                page
                                            )
                                        }
                                    >

                                        {page}

                                    </button>

                                )
                            )}

                        </div>


                        {/* SUIVANT */}

                        <button
                            type="button"
                            className="pagination-btn"
                            disabled={
                                currentPage === totalPages
                            }
                            onClick={() =>
                                changerPage(
                                    currentPage + 1
                                )
                            }
                        >

                            <span>
                                Suivant
                            </span>

                            ›

                        </button>

                    </div>

                </div>

            )}

        </div>
    );
};


export default FinanceTable;